/**
 * Suggestion List Component for v2v2 SWFL Farmers Market Application
 * Dropdown container for grouped search suggestions with keyboard navigation
 */



import { useEffect, useRef, useCallback } from 'react';
import { Clock, Package, Folder, Store, MapPin, Leaf, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SuggestionItem } from './suggestion-item';
import type { SuggestionGroup, SearchSuggestion, SuggestionType } from '@/types/suggestions';

interface SuggestionListProps {
  groups: SuggestionGroup[];
  isLoading: boolean;
  selectedIndex: number;
  onSelect: (suggestion: SearchSuggestion) => void;
  onClose: () => void;
  query: string;
  isOpen: boolean;
}

const groupIcons: Record<SuggestionType, React.ComponentType<{ className?: string }>> = {
  product: Package,
  category: Folder,
  vendor: Store,
  market: MapPin,
  recent: Clock,
  seasonal: Leaf,
};

export function SuggestionList({ 
  groups, 
  isLoading, 
  selectedIndex, 
  onSelect, 
  onClose,
  query,
  isOpen 
}: SuggestionListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Flatten all suggestions for keyboard navigation
  const flattenSuggestions = useCallback(() => {
    return groups.flatMap(group => group.suggestions);
  }, [groups]);

  // Don't render if closed or no suggestions and not loading
  if (!isOpen) return null;
  
  if (!isLoading && groups.length === 0 && !query) {
    return null;
  }

  // Calculate total suggestions count
  const totalSuggestions = flattenSuggestions().length;
  const showEmptyState = !isLoading && totalSuggestions === 0 && query.length > 0;

  return (
    <div 
      className="absolute top-full left-0 right-0 mt-1 z-50 animate-in fade-in-0 zoom-in-95 duration-200"
      ref={listRef}
    >
      <div className="bg-popover border rounded-lg shadow-lg overflow-hidden">
        {/* Header with close button */}
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
          <span className="text-xs font-medium text-muted-foreground">
            {query ? 'Suggestions' : 'Quick Access'}
          </span>
          {query && (
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-accent transition-colors"
              aria-label="Close suggestions"
            >
              <X className="w-3 h-3 text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="p-4 text-center">
            <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              Searching...
            </div>
          </div>
        )}

        {/* Suggestions Groups */}
        {!isLoading && groups.map((group) => (
          <div key={group.title} className="border-b last:border-b-0">
            {/* Group Header */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-muted/20">
              {(() => {
                const Icon = groupIcons[group.suggestions[0]?.type] || Package;
                return <Icon className="w-3 h-3 text-muted-foreground" />;
              })()}
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {group.title}
              </span>
            </div>

            {/* Group Items */}
            <ul role="listbox" aria-label={group.title}>
              {group.suggestions.map((suggestion, index) => {
                const globalIndex = flattenSuggestions().findIndex(
                  s => s.id === suggestion.id
                );
                
                return (
                  <li key={suggestion.id}>
                    <SuggestionItem
                      suggestion={suggestion}
                      isSelected={globalIndex === selectedIndex}
                      onClick={() => onSelect(suggestion)}
                      query={query}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {/* Empty State */}
        {showEmptyState && (
          <div className="p-4 text-center">
            <p className="text-sm text-muted-foreground">
              No suggestions found for "{query}"
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Try a different search term
            </p>
          </div>
        )}

        {/* Keyboard Hints */}
        <div className="flex items-center justify-between px-3 py-2 border-t bg-muted/30 text-[10px] text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-accent rounded text-foreground">↑↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-accent rounded text-foreground">↵</kbd>
              Select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-accent rounded text-foreground">esc</kbd>
              Close
            </span>
          </div>
          <span>
            {totalSuggestions} result{totalSuggestions !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    </div>
  );
}
